import { useQuery } from "@tanstack/react-query";
import CreditService from "services/CreditService";
import { ALL_TRANSACTIONS } from "services/constants";

interface TransactionHistoryParams {
  dateId?: number;
  tabId?: string;
  page?: number;
  recruiterId?: string;
  transactionType?: string;
}

interface TransactionData {
  transactionLabel: string;
  lastUpdated: string;
  status: string;
  credits: string;
  expirationDate: string;
  transactionDate: string;
  transactionTime: string;
  closingBalance: string;
  activity: string;
  isInvoiceAvailable: boolean;
  invoiceEmail: string;
  orderId: string;
  [key: string]: any;
}

interface TabData {
  id: string;
  label: string;
  count: number;
  is_selected?: boolean;
}

interface DateFilter {
  label: string;
  value: number;
}

interface TransactionHistoryResponse {
  transactionData: TransactionData[];
  tabs: TabData[];
  dateFilter: number;
  recruiterName: string;
  totalCount: number;
}

export const transactionHistoryKeys = {
  all: ["transactionHistory"] as const,
  lists: () => [...transactionHistoryKeys.all, "list"] as const,
  list: (filters: TransactionHistoryParams) =>
    [...transactionHistoryKeys.lists(), filters] as const
};

/**
 * React Query hook to fetch transaction history
 * Uses React Query's built-in caching mechanism
 *
 * @param params - Transaction history query parameters
 * @param options - React Query options
 * @returns Query result with transaction history data
 */
export const useTransactionHistory = (
  params: TransactionHistoryParams = {},
  options: {
    enabled?: boolean;
    staleTime?: number;
    refetchOnWindowFocus?: boolean;
  } = {}
) => {
  const { dateId = 90, tabId = ALL_TRANSACTIONS, page = 1, recruiterId="" } = params;

  return useQuery({
    queryKey: transactionHistoryKeys.list({
      dateId,
      tabId,
      page
    }),
    queryFn: async (): Promise<TransactionHistoryResponse> => {
      try {
        const response = await CreditService.getTransactionHistory({
          dateId,
          tabId,
          page,
          recruiterId
        });

        return response;
      } catch (error) {
        console.error("Error fetching transaction history:", error);
        throw error;
      }
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
    ...options
  });
};
